import { STARTING_LEVEL, type SkillLevels } from './maths-skills.js'
import { assignmentSpace, ASSIGNMENT_CAP, type ClueKind } from './riddle-solve.js'

/**
 * What she is getting better at, in Rocky's Riddles.
 *
 * A skill here is a **kind of clue**, which is genuinely different from every
 * other game's idea of one. Monkey's skills and Turtley's each know how to
 * build a question; a clue kind cannot, because a clue is only worth writing
 * once you know the row, the deal and the question it must not give away.
 *
 * So `RiddleSkill` has no `build()`. Giving it one for the sake of matching
 * the other two would produce a method that lied about what it built.
 */
export interface RiddleSkill {
  readonly key: string
  /** Shown to a parent, not to the player. */
  readonly label: string
  readonly unlocksAt: number
  readonly kind: ClueKind
}

/**
 * The clue kinds, easiest first.
 *
 * The ladder is about what a sentence asks her to hold in her head, not about
 * arithmetic. "Sky took the pear" is a fact. "Hoppity did not take the apple"
 * is a fact about what is not so, which is a real step up at eight. Everything
 * from `end` on asks her to read the row as well as the sentence.
 */
export const RIDDLE_SKILLS: readonly RiddleSkill[] = [
  {
    key: 'riddle.direct',
    label: 'Taking a clue at its word',
    unlocksAt: 0,
    kind: 'direct',
  },
  {
    key: 'riddle.negative',
    label: 'Ruling somebody out',
    unlocksAt: 0,
    kind: 'negative',
  },
  {
    key: 'riddle.either',
    label: 'Narrowing it to two',
    unlocksAt: 3,
    kind: 'either',
  },
  {
    key: 'riddle.end',
    label: 'Reading the ends of the row',
    unlocksAt: 4,
    kind: 'end',
  },
  {
    key: 'riddle.neighbour',
    label: 'Working from who sat beside whom',
    unlocksAt: 5,
    kind: 'neighbour',
  },
  {
    key: 'riddle.link',
    label: 'Joining two clues about one teddy',
    unlocksAt: 7,
    kind: 'link',
  },
  {
    key: 'riddle.side',
    label: 'Working from what is in front and behind',
    unlocksAt: 8,
    kind: 'side',
  },
]

export function riddleSkillOf(kind: ClueKind): RiddleSkill {
  const skill = RIDDLE_SKILLS.find((candidate) => candidate.kind === kind)
  // Not a `?? throw` on a maybe: the list above is exhaustive over ClueKind by
  // construction, and a test asserts it, so reaching here is a missing entry
  // rather than a bad input.
  if (!skill) throw new Error(`No skill for clue kind ${kind}`)

  return skill
}

export function overallRiddleLevel(levels: SkillLevels): number {
  const known = RIDDLE_SKILLS.map((skill) => levels[skill.key]).filter(
    (level): level is number => level !== undefined,
  )

  if (known.length === 0) return STARTING_LEVEL

  return known.reduce((total, level) => total + level, 0) / known.length
}

/**
 * How big and how hard a riddle is at a given level.
 *
 * Note what is **not** here: a number of clues. Clue count is not a difficulty
 * measure — a riddle with eight clues can be easier than one with four, and
 * tuning on it would make riddles longer rather than harder. What the
 * generator needs is how many teddies, which kinds of clue it may write, and
 * how much help to leave in.
 */
export interface RiddleShape {
  readonly teddies: number
  /** One category, or two once they cross. */
  readonly categories: number
  readonly kinds: readonly ClueKind[]
  /**
   * Extra true clues added after the puzzle is already solvable.
   *
   * These make a riddle **easier**, not harder — every one of them is another
   * way in, and a stuck child needs a way in more than she needs elegance. So
   * they taper to nothing as she improves, and any test of minimality has to
   * be run with them off.
   */
  readonly redundant: number
  /**
   * Whether to start the clue set with a clue that names somebody outright.
   *
   * A riddle made only of "did not" is solvable and horrible: there is nowhere
   * to put your finger while you think. A beginner gets one fact to stand on,
   * and loses it once the standing is the easy part.
   */
  readonly foothold: boolean
}

const EASIEST: readonly ClueKind[] = ['direct', 'negative']

/**
 * Which kinds are open at a level.
 *
 * `direct` is in every one of these, and must be: the generator's guarantee of
 * termination is that the pool always contains enough direct clues to pin the
 * deal outright. Take it away at any level and the greedy loop has no floor.
 */
function kindsFor(level: number): readonly ClueKind[] {
  return RIDDLE_SKILLS.filter((skill) => skill.unlocksAt <= level).map((skill) => skill.kind)
}

export function shapeForLevel(level: number): RiddleShape {
  const at = Math.max(0, Math.min(10, Math.round(level)))

  /*
   * Teddies never go down as she goes up, and the second category arrives on
   * its own rather than alongside a fifth teddy. Two things getting harder at
   * once is how a ladder produces a step nobody can climb — and it is also
   * what puts the search space up: five teddies and two categories is 14,400
   * deals, which is the largest thing this game ever brute-forces and is
   * deliberately kept for the very top.
   */
  const teddies = at <= 2 ? 3 : at <= 8 ? 4 : 5
  const categories = at >= 7 ? 2 : 1
  // Two spare ways in for a beginner, one in the middle, none once the point
  // is the deduction itself.
  const redundant = at <= 1 ? 2 : at <= 5 ? 1 : 0

  return {
    teddies,
    categories,
    kinds: at <= 2 ? EASIEST : kindsFor(at),
    redundant,
    foothold: at <= 5,
  }
}

/**
 * Whether every shape the ladder can produce is small enough to brute force.
 *
 * Exported so a test can walk `shapeForLevel(0..10)` rather than trusting the
 * arithmetic above. The failure this prevents is not an error anybody sees —
 * it is her tablet going quiet at the start of a round — so it is worth
 * catching at build time on the day somebody adds a third category.
 */
export function shapeFitsCap(shape: RiddleShape): boolean {
  return assignmentSpace(shape.teddies, shape.categories) <= ASSIGNMENT_CAP
}
